import type { IEdgeRegistration } from '@experiences/interfaces';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridHeader,
    ApDataGridHeaderButton,
    ApDataGridRowActions,
    ApDataGridRowButton,
    ApDataGridWrapper,
    ApIconButton,
    ApTooltip,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import { useBreadcrumbs } from '../../common/providers/BreadcrumbProvider';
import UiPageContainer from '../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../organizationsettings/AdminBreadCrumbs';
import useEdgeSettingsViewModel from './EdgeSettingsViewModel';

const EdgeSettingsPageComponent: React.FC = () => {
    const { breadcrumbs } = useBreadcrumbs();
    const { formatMessage: translate } = useIntl();
    const {
        handleAddEdge, edgeRegistrationData, loading, refresh, deleteEdge,
    } = useEdgeSettingsViewModel();

    return (
        <UiPageContainer
            position='left'
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadcrumbs} />}
        >
            <div
                style={{ width: '100%' }}
                data-cy='edge-registrations-grid-container'>
                <ApDataGridWrapper<IEdgeRegistration>
                    data={edgeRegistrationData ?? []}
                    toggleColumns
                    selectable={false}
                    loading={loading}
                    refreshable
                    refresh={refresh}
                    dataCy='edge-registrations-grid'
                >
                    <ApDataGridHeader<IEdgeRegistration>>
                        <ApDataGridHeaderButton<IEdgeRegistration>
                            id='addEdge'
                            key='addEdge'
                            type='main'
                            buttonType='mat-flat-button'
                            color='primary'
                            text={translate({ id: 'CLIENT_ADD_EDGE' })}
                            label={translate({ id: 'CLIENT_ADD_EDGE' })}
                            onClick={handleAddEdge}
                            dataCy='add-edge-button'
                            disabled={(edgeRegistrationData?.length ?? 0) >= 1} // Allow only one Edge per tenant
                            visible
                        />
                    </ApDataGridHeader>
                    <ApDataGridColumn<IEdgeRegistration>
                        property='id'
                        width="20%"
                        title={translate({ id: 'CLIENT_EDGE_ID' })}
                    />
                    <ApDataGridColumn<IEdgeRegistration>
                        property='serverAddress'
                        title={translate({ id: 'CLIENT_EDGE_SERVER_ADDRESS' })}
                    />
                    <ApDataGridColumn<IEdgeRegistration>
                        property='fqdn'
                        title={translate({ id: 'CLIENT_EDGE_FQDN' })}
                    />
                    <ApDataGridRowActions>
                        <ApDataGridRowButton<IEdgeRegistration>
                            id='delete'
                            dataCy='delete-edge-button'
                            render={row => {
                                const label = translate({ id: 'CLIENT_DELETE' });
                                return (<ApTooltip content={label}>
                                    <ApIconButton
                                        label={label}
                                        data-cy='delete-button'
                                        color="secondary"
                                        onClick={() => deleteEdge(row)}
                                    >
                                        <DeleteOutlinedIcon aria-label={label} />
                                    </ApIconButton>
                                </ApTooltip>);
                            }}
                        />
                    </ApDataGridRowActions>
                    <ApDataGridFooter hidden
                    />
                </ApDataGridWrapper>
            </div>
        </UiPageContainer>
    );
};

export default EdgeSettingsPageComponent;
